import { pick } from 'js/utils/ramda.util';
import { useCallback, useMemo } from 'react';
import { getDateForIms } from 'js/hooks/useFormatter/helpers';
import { useComponentTranslations } from 'js/hooks/component/useComponentTranslations';

import * as helpers from '../helpers';
import * as actions from '../store/actions';
import { useOnFilterChange } from './useOnFilterChange';
import { useAnotherPeriodFilter } from './useAnotherPeriodFilter';
import { useDynamicReducerSelector, useDynamicReducerDispatch } from '../store';

export const useTableHeader = () => {
    const onChange = useOnFilterChange();
    const dispatch = useDynamicReducerDispatch();
    const translate = useComponentTranslations();
    const anotherPeriodFilter = useAnotherPeriodFilter();
    const {
        from,
        until,
        period,
        isLoading,
        limitType,
        enabledLimitTypes
    } = useDynamicReducerSelector(pick([
        'from',
        'until',
        'period',
        'isLoading',
        'limitType',
        'enabledLimitTypes'
    ]));

    const headerValidate = useCallback(() => {
        if (helpers.intoDate(from) > helpers.intoDate(until)) {
            return { from: translate('filter.error.timePeriod') };
        }
    }, [from, until, translate]);

    const headerInitialValues = useMemo(() => ({
        from,
        until,
        period,
        limitType
    }), [from, until, period, limitType]);

    const onAnotherPeriodApply = useCallback((values) => {
        const newStartDate = getDateForIms(new Date(values.from));

        dispatch(actions.updateStartDate(newStartDate));
    }, [dispatch]);

    return useMemo(() => ({
        filters: [
            {
                id: 0,
                onChange,
                name: 'period',
                disabled: isLoading,
                label: translate('filter.title.startDate'),
                options: helpers.getPeriodOptions(translate),
                'aria-label': translate(['filter.title.startDate', 'playtech.mobile.system.select.aria-label'])
            },
            {
                id: 1,
                onChange,
                name: 'limitType',
                disabled: isLoading,
                label: translate('filter.title.limitType'),
                options: helpers.getLimitTypeOptions(enabledLimitTypes, translate),
                'aria-label': translate(['filter.title.limitType', 'playtech.mobile.system.select.aria-label'])
            }
        ],
        headerValidate,
        anotherPeriodFilter,
        headerInitialValues,
        onAnotherPeriodApply
    }), [
        translate, isLoading, onChange, headerValidate, enabledLimitTypes,
        anotherPeriodFilter, onAnotherPeriodApply, headerInitialValues
    ]);
};
